import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Flag, 
  MapPin,
  Calendar,
  Users,
  Trophy,
  Loader2,
  Play,
  Check,
  Lock,
  ChevronLeft
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RoundDetailPage = () => {
  const { roundId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedHole, setSelectedHole] = useState(1);
  const [scoreInput, setScoreInput] = useState({
    strokes: '',
    putts: '',
    fairway_hit: null,
    gir: null
  });

  useEffect(() => {
    fetchRound();
    // Poll for updates in live rounds
    const interval = setInterval(fetchRound, 10000);
    return () => clearInterval(interval);
  }, [roundId]);

  const fetchRound = async () => {
    try {
      const response = await axios.get(`${API_URL}/rounds/${roundId}`);
      setRound(response.data);
    } catch (error) {
      console.error('Failed to fetch round:', error);
      toast.error('Round not found');
    } finally {
      setLoading(false);
    }
  };

  const isPlayer = round?.player_uids?.includes(user?.uid);
  const isAdmin = round?.admins?.includes(user?.uid);

  const handleStartRound = async () => {
    try {
      await axios.post(`${API_URL}/rounds/${roundId}/start`);
      toast.success('Round started!');
      fetchRound();
    } catch (error) {
      toast.error('Failed to start round');
    }
  };

  const handleJoinRound = async () => {
    try {
      await axios.post(`${API_URL}/rounds/${roundId}/join`);
      toast.success('Joined round!');
      fetchRound();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleSubmitScore = async () => {
    if (!scoreInput.strokes) {
      toast.error('Please enter strokes');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/rounds/${roundId}/scores`, {
        hole_number: selectedHole,
        strokes: parseInt(scoreInput.strokes),
        putts: scoreInput.putts ? parseInt(scoreInput.putts) : null,
        fairway_hit: scoreInput.fairway_hit,
        gir: scoreInput.gir
      });
      toast.success(`Score saved for hole ${selectedHole}`);
      setScoreInput({ strokes: '', putts: '', fairway_hit: null, gir: null });
      if (selectedHole < round.holes) {
        setSelectedHole(selectedHole + 1);
      }
      fetchRound();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit score');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalizeRound = async () => {
    if (!confirm('Finalize this round? This will lock all scores.')) return;
    
    try {
      await axios.post(`${API_URL}/rounds/${roundId}/finalize`);
      toast.success('Round finalized!');
      fetchRound();
    } catch (error) {
      toast.error('Failed to finalize round');
    }
  };

  const getMyScore = (holeNumber) => {
    return round?.scores?.find(s => s.uid === user?.uid && s.hole_number === holeNumber);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500/20 text-blue-400';
      case 'live': return 'bg-green-500/20 text-green-400';
      case 'final': return 'bg-gold/20 text-gold';
      default: return 'bg-white/10 text-white/70';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!round) {
    return (
      <div className="text-center py-16">
        <Flag className="w-16 h-16 text-cream/30 mx-auto mb-4" />
        <h2 className="text-xl font-display text-white mb-2">Round Not Found</h2>
        <Link to="/rounds">
          <Button variant="outline" className="mt-4">Back to Rounds</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="round-detail-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/rounds')}
        className="text-cream/70 hover:text-white"
      >
        <ChevronLeft className="w-4 h-4 mr-2" />
        Back to Rounds
      </Button>

      {/* Round Header */}
      <Card className="bg-gradient-to-r from-fairway to-deep-green border-0">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className={getStatusColor(round.status)}>
                  {round.status === 'live' && <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />}
                  {round.status}
                </Badge>
                <Badge variant="outline" className="border-white/30 text-white capitalize">
                  {round.type}
                </Badge>
              </div>
              <h1 className="font-display font-bold text-2xl text-white mb-2">
                {round.course?.name || 'Course TBD'}
              </h1>
              {round.course && (
                <p className="text-cream/70 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {round.course.city}, {round.course.state}
                </p>
              )}
            </div>

            <div className="flex flex-col md:items-end gap-2">
              <div className="flex items-center gap-2 text-cream/70">
                <Calendar className="w-4 h-4" />
                {format(new Date(round.tee_time), 'MMMM d, yyyy • h:mm a')}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-cream/70">
                  <Users className="w-4 h-4 inline mr-1" />
                  {round.player_uids?.length || 0} players
                </span>
                <span className="text-cream/70">
                  {round.holes} holes • {round.format}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            {round.status === 'scheduled' && isAdmin && (
              <Button onClick={handleStartRound} className="btn-gold" data-testid="start-round-btn">
                <Play className="w-4 h-4 mr-2" />
                Start Round
              </Button>
            )}
            {round.status === 'scheduled' && !isPlayer && (
              <Button onClick={handleJoinRound} className="btn-gold" data-testid="join-round-btn">
                <Users className="w-4 h-4 mr-2" />
                Join Round
              </Button>
            )}
            {round.status === 'live' && isAdmin && (
              <Button 
                onClick={handleFinalizeRound} 
                variant="outline"
                className="border-gold/50 text-gold hover:bg-gold hover:text-deep-green"
                data-testid="finalize-round-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Finalize Round
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Score Entry (for live rounds) */}
        {round.status === 'live' && isPlayer && (
          <Card className="bg-white/5 border-white/10 lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-white font-display">Enter Score</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Hole Selector */}
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: round.holes }, (_, i) => i + 1).map((hole) => {
                  const myScore = getMyScore(hole);
                  return (
                    <button
                      key={hole}
                      onClick={() => setSelectedHole(hole)}
                      className={cn(
                        "w-10 h-10 rounded-sm font-semibold transition-colors relative",
                        selectedHole === hole
                          ? "bg-gold text-deep-green"
                          : myScore
                          ? "bg-green-500/20 text-green-400"
                          : "bg-white/10 text-white hover:bg-white/20"
                      )}
                      data-testid={`hole-${hole}`}
                    >
                      {hole}
                      {myScore?.locked && (
                        <Lock className="w-3 h-3 absolute -top-1 -right-1 text-gold" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-cream/70 text-sm mb-1 block">Strokes</label>
                  <Input
                    type="number"
                    min="1"
                    max="15"
                    value={scoreInput.strokes}
                    onChange={(e) => setScoreInput({ ...scoreInput, strokes: e.target.value })}
                    placeholder="--"
                    className="bg-white/10 border-white/20 text-white text-center text-2xl font-bold h-14"
                    data-testid="strokes-input"
                  />
                </div>
                <div>
                  <label className="text-cream/70 text-sm mb-1 block">Putts (optional)</label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={scoreInput.putts}
                    onChange={(e) => setScoreInput({ ...scoreInput, putts: e.target.value })}
                    placeholder="--"
                    className="bg-white/10 border-white/20 text-white text-center"
                    data-testid="putts-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={scoreInput.fairway_hit === true ? "default" : "outline"}
                    className={cn(
                      "flex-1",
                      scoreInput.fairway_hit === true ? "bg-green-500" : "border-white/20 text-white"
                    )}
                    onClick={() => setScoreInput({ ...scoreInput, fairway_hit: scoreInput.fairway_hit === true ? null : true })}
                  >
                    FIR
                  </Button>
                  <Button
                    type="button"
                    variant={scoreInput.gir === true ? "default" : "outline"}
                    className={cn(
                      "flex-1",
                      scoreInput.gir === true ? "bg-green-500" : "border-white/20 text-white"
                    )}
                    onClick={() => setScoreInput({ ...scoreInput, gir: scoreInput.gir === true ? null : true })}
                  >
                    GIR
                  </Button>
                </div>
              </div>

              <Button 
                onClick={handleSubmitScore}
                className="w-full btn-gold"
                disabled={submitting || !scoreInput.strokes}
                data-testid="submit-score-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Hole {selectedHole}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard */}
        <Card className={cn(
          "bg-white/5 border-white/10",
          round.status === 'live' && isPlayer ? "lg:col-span-2" : "lg:col-span-3"
        )}>
          <CardHeader>
            <CardTitle className="text-white font-display flex items-center gap-2">
              <Trophy className="w-5 h-5 text-gold" />
              Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            {round.players?.length === 0 ? (
              <p className="text-cream/60 text-center py-8">No players yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-cream/60 font-normal">Pos</th>
                      <th className="text-left py-3 px-4 text-cream/60 font-normal">Player</th>
                      <th className="text-center py-3 px-4 text-cream/60 font-normal">Thru</th>
                      <th className="text-center py-3 px-4 text-cream/60 font-normal">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {round.players
                      ?.sort((a, b) => (a.current_total_strokes || 999) - (b.current_total_strokes || 999))
                      .map((player, index) => (
                        <tr 
                          key={player.uid} 
                          className={cn(
                            "border-b border-white/5",
                            player.uid === user?.uid && "bg-gold/10"
                          )}
                        >
                          <td className="py-3 px-4">
                            <span className={cn(
                              "w-8 h-8 rounded-full inline-flex items-center justify-center font-bold",
                              index === 0 && "bg-gold text-deep-green"
                            )}>
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-white font-semibold">
                            {player.display_name}
                            {player.handicap && (
                              <span className="text-cream/50 text-sm ml-2">
                                ({player.handicap})
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center text-cream/70">
                            {player.current_hole || 0}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-white font-bold text-lg">
                              {player.current_total_strokes || '--'}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RoundDetailPage;
